import { Component, OnInit, inject, DestroyRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap, catchError, first } from 'rxjs/operators';
import { of } from 'rxjs';
import { CompanyGroup } from '@shared/entities/companyGroup';
import { CompanyGroupService } from '@shared/services/api/company-group.service';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-company-group-form',
  templateUrl: './company-group-form.component.html',
  styleUrl: './company-group-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyGroupFormComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _fb = inject(FormBuilder);
  private _route = inject(ActivatedRoute);
  private _location = inject(Location);
  private _router = inject(Router);
  private _companyGroupService = inject(CompanyGroupService);
  private _themeService = inject(ThemeService);

  isLight = true;

  form!: FormGroup;
  isEdit = false;
  isLoading = false;
  isSuccess = false;
  isError = false;
  isDeleteSuccess = false;
  isDeleteError = false;
  errorMessage = '';

  private _groupId: string | null = null;
  _group: CompanyGroup = new CompanyGroup();

  ngOnInit(): void {
    this.form = this._fb.group({
      name: ['', Validators.required],
      description: ['']
    });

    this._themeService.isLight$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isLight) => {
      this.isLight = isLight;
      this._cdr.markForCheck();
    });

    this._route.params.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params) => {
      this._groupId = params['id'] || null;
      this.isEdit = !!this._groupId;
      if (this.isEdit) this._loadData();
      this._cdr.markForCheck();
    });
  }

  private _loadData(): void {
    this.isLoading = true;
    this._companyGroupService.findByFilter(this._groupId!).pipe(
      first(),
      tap((paginated) => {
        const group = paginated?.data?.[0];
        if (group) {
          this._group = group;
          this.form.patchValue({
            name: group.name,
            description: group.description
          });
        }
        this.isLoading = false;
        this._cdr.markForCheck();
      }),
      catchError(() => {
        this.isLoading = false;
        this._cdr.markForCheck();
        return of(null);
      })
    ).subscribe();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this._resetFlags();

    const group = Object.assign(new CompanyGroup(), this.form.value);

    const request$ = this.isEdit
      ? this._companyGroupService.editCompanyGroup(group, this._groupId!)
      : this._companyGroupService.saveCompanyGroup(group);

    request$.pipe(
      first(),
      tap(() => {
        this.isLoading = false;
        this.isSuccess = true;
        this._companyGroupService.refreshFilters();
        this._cdr.markForCheck();
      }),
      catchError((err) => {
        this.isLoading = false;
        this.isError = true;
        this.errorMessage = err?.error?.message || 'Errore durante il salvataggio';
        this._cdr.markForCheck();
        return of(null);
      })
    ).subscribe();
  }

  onDelete(): void {
    if (!this._groupId) return;

    this.isLoading = true;
    this._resetFlags();

    this._companyGroupService.deleteCompanyGroup(this._groupId).pipe(
      first(),
      tap(() => {
        this.isLoading = false;
        this.isDeleteSuccess = true;
        this._companyGroupService.refreshFilters();
        this._cdr.markForCheck();
      }),
      catchError((err) => {
        this.isLoading = false;
        this.isDeleteError = true;
        this.errorMessage = err?.error?.message || 'Errore durante la cancellazione';
        this._cdr.markForCheck();
        return of(null);
      })
    ).subscribe();
  }

  goToList(): void {
    this._router.navigate(['/company-group']);
  }

  goBack(): void {
    this._router.navigate(['/company-group']);
  }

  private _resetFlags(): void {
    this.isSuccess = false;
    this.isError = false;
    this.isDeleteSuccess = false;
    this.isDeleteError = false;
    this.errorMessage = '';
  }
}
